import { Component, OnInit, ViewChildren, QueryList, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/finally';
import 'rxjs/add/observable/zip';
import { ToastService } from 'libs/mdbootstrap/typescripts/pro/index';
import { CompleterComponent } from 'libs/mdbootstrap/typescripts/pro/autocomplete';
import * as Models from 'app/core/models';
import { AuthenticationService } from 'app/core/authentication/authentication.service';
import { ModalService } from 'app/modal/modal.service';
import { LookupService } from 'app/core/lookup/lookup.service';
import { I18nService } from 'app/core/i18n.service';
import { ProcessService } from 'app/core/process/process.service';
import { PageTitleService } from '../shared/helpers/pageTitle.service';

@Component({
  selector: 'app-edit-process',
  templateUrl: './editProcess.component.html',
  styleUrls: ['./editProcess.component.scss'],
})
export class EditProcessComponent implements OnInit, OnDestroy {

  @ViewChildren(CompleterComponent) completers: QueryList<CompleterComponent>;

  private changeDetectorInterval: number;

  textHtmlEditorPlaceHolder: string;
  stepTypes: Models.StepType[] = [];
  showContent = false;
  process: Models.Process = null;
  isCollapsed: boolean;
  isLoading = true;
  private error: any = null;
  private processId: number = null;
  private showError = false;
  private isSaving = false;
  private stepTypePrefixes: Models.StepTypePrefix[] = [];
  private stepTypesSelect: Array<any> = [];
  private titleAutoCompletePlaceHolder: string;
  private titleAutoCompletePlaceHolder_NoStepType: string;
  private generalErrorMessage: string;
  private checkBoxText: string;
  private processMedias: Array<Models.Media> = [];
  private processLanguage: Models.Languages = Models.Languages.enUS;

  constructor(private formBuilder: FormBuilder,
    private processService: ProcessService,
    private lookupService: LookupService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private actRoute: ActivatedRoute,
    private translateService: TranslateService,
    private modalService: ModalService,
    private toastrService: ToastService,
    private pageTitleService: PageTitleService,
    private i18nService: I18nService,
    public cd: ChangeDetectorRef) {

  }

  ngOnInit() {
    this.cd.detach();
    this.changeDetectorInterval = window.setInterval(() => {
      this.cd.detectChanges();
    }, 200);
    this.processLanguage = Models.Languages.enUS;
    this.isLoading = true;
    this.processId = +this.actRoute.snapshot.paramMap.get('id');
    this.processLanguage = Models.Languages.enUS;
    this.translateTexts();
    this.getProcess();
  }

  ngOnDestroy(): void {
    this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, Models.Languages.enUS);
    window.clearInterval(this.changeDetectorInterval);
    this.cd.reattach();
  }

  getProcess() {
    Observable
      .zip(
        this.processService.getDraft(this.processId),
        this.processService.getProcessMedias(this.processId))
      .finally(() => {
        if (!this.error) {
          this.getStepTypeData();
        } else {
          this.isLoading = false;
        }
      })
      .subscribe(([process, processMedias]) => {
        if (process.stages == null) {
          process.stages = [];
        }
        if (process.stages.length === 0) {
          process.stages.push(new Models.ProcessStage());
        }

        this.processLanguage = !process.isRTL ? Models.Languages.enUS : Models.Languages.heIL;
        this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, this.processLanguage);
        this.translateTexts();
        this.process = process;
        this.pageTitleService.setTitle(this.actRoute.data['value'].titleKey, this.process.title);
        this.processData();
        this.error = null;
        this.showError = false;
        this.processMedias = processMedias;
      }, error => {
        this.error = error;
        this.showContent = false;
        this.showError = true;
      });
  }

  getStepTypeData(): void {
    Observable
      .zip(
        this.lookupService.getStepTypes(this.processLanguage),
        this.lookupService.getStepTypePrefixes(this.processLanguage)
      )
      .finally(() => {
        this.isLoading = false;
      })
      .subscribe(([stepTypes, stepTypePrefixes]) => {
        this.stepTypes = stepTypes;
        this.createStepTypeSelect();
        this.stepTypePrefixes = stepTypePrefixes;
        this.error = null;
        this.showError = false;
        this.showContent = true;
      }, error => {
        this.error = error;
        this.showContent = false;
        this.showError = true;
      });
  }

  get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  get errorMessage(): string {
    if (this.error) {
      const errorBody = this.error._body ? JSON.parse(this.error._body) : null;
      if (errorBody && errorBody.error) {
        return errorBody.error;
      }
    }
    return this.generalErrorMessage;
  }

  processData(): void {
    this.process.stages.forEach(stage => {
      stage.steps.forEach(step => {
        step.showTasks = !!(step.tasks && step.tasks.length);
      });
    });
  }

  getStepTypePrefixesSelect(stepTypeId: number): Array<string> {
    if (!stepTypeId) {
      return this.stepTypePrefixes.map(x => x.text);
    } else {
      return this.stepTypePrefixes.filter(x => x.stepTypeId === stepTypeId).map(x => x.text);
    }
  }

  getStepTypeImage(stepTypeId: number): string {
    if (stepTypeId) {
      return this.stepTypes.find(x => x.stepTypeId === stepTypeId).imageUrl;
    } else {
      return 'https://proactiviewstaging.blob.core.windows.net/step-type-icons/default.png';
    }
  }

  change() {
    if (this.canProcessPublished()) {
      this.saveDraft();
    }
  }

  saveDraft() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.processService.saveDraft(this.process)
      .finally(() => {
        this.isSaving = false;
      })
      .subscribe();
  }

  publish() {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.PROCESS_PUBLISH,
      modalAction: Models.ModalAction.OPEN,
      parameters: [this.processId, this.process.processVersionId, this.process.tags, this.process.isPremium,
      this.process.allowAssistance, this.processMedias, this.process.coverMediaId, this.process.coverSmallUrl, this.process.processAuthorization, this.process.usageType]
    };
    this.modalService.command(data);
    const subscription = this.modalService.returnEvent$.subscribe((resultData: Models.ModalResult) => {
      if (resultData.modalType === Models.ModalType.PROCESS_PUBLISH) {
        subscription.unsubscribe();
        const newData: Models.ModalContext = {
          modalType: Models.ModalType.PROCESS_PUBLISH,
          modalAction: Models.ModalAction.CLOSE,
          parameters: []
        };
        this.modalService.command(newData);
        const result: boolean = resultData.result;
        if (result) {
          this.translateService.get('PROCESS.EDIT.PUBLISH_SUCCESS_MSG').subscribe((successMsg) => {
            this.toastrService.success(successMsg);

            const nextData: Models.ModalContext = {
              modalType: Models.ModalType.PROCESS_COMPLETE,
              modalAction: Models.ModalAction.OPEN,
              parameters: [Models.Process_Complete_Mode.CREATION, this.process.title, this.process.processId,
              this.process.coverSmallUrl, false, false, this.process.isRTL]
            };
            this.modalService.command(nextData);
          });
        }
      }
    });
  }

  canProcessPublished(): boolean {
    return this.processService.canPublishProcess(this.process);
  }

  duplicateStep(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    const stepIndex: number = stage.steps.findIndex(x => x.order === stepOrder);
    const newStep = Object.assign({}, step);
    newStep.tasks = step.tasks.slice(0);
    stage.steps.splice(stepIndex + 1, 0, newStep);

    let i: number;
    for (i = stepIndex + 1; i < stage.steps.length; i++) {
      stage.steps[i].order++;
    }

    this.change();
    this.scrollToElementByIdWhenCreated(this.getStepElementId(stageOrder, stepOrder + 1));
  }

  deleteStep(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    if (stage.steps.length <= 1) {
      this.translateService.get('PROCESS.EDIT.STEP_DELETE_ERROR').subscribe((translatedMsg) => {
        this.toastrService.error(translatedMsg);
      });
      return;
    }

    const stepIndex: number = stage.steps.findIndex(x => x.order === stepOrder);

    stage.steps.splice(stepIndex, 1);

    let i: number;
    for (i = stepIndex; i < stage.steps.length; i++) {
      stage.steps[i].order--;
    }

    this.change();
  }

  changeStepType(step: Models.ProcessStep, stepTypeId: number, stageOrder: number): void {
    const previousStepTypeId = step.stepTypeId;
    step.stepTypeId = stepTypeId;

    const completerSelector: string = 'completer_' + stageOrder + '_' + step.order;
    const current: CompleterComponent = this.completers.find(x => x.inputId === completerSelector);
    if (current != null) {
      current.close();
      current.completer.clear();
      if (!previousStepTypeId) {
        current.completer.open();
      }
    }

    this.change();
  }

  moveStepUp(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    const stepIndex: number = stage.steps.findIndex(x => x.order === stepOrder);

    if (stepOrder >= 2) {
      const stepOrder2 = stepOrder - 1;

      const step2: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder2);

      const stepIndex2: number = stage.steps.findIndex(x => x.order === stepOrder2);

      step.order--;
      step2.order++;

      stage.steps[stepIndex] = step2;
      stage.steps[stepIndex2] = step;

      this.scrollToElementByIdWhenCreated(this.getStepElementId(stageOrder, step.order));
      this.change();
    } else if (stepOrder === 1 && stage.steps.length > 1 && stage.order > 1) {
      const nextStage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder - 1);

      stage.steps.splice(stepIndex, 1);

      for (let i = 0; i < stage.steps.length; i++) {
        stage.steps[i].order--;
      }

      step.order = nextStage.steps.length + 1;

      nextStage.steps.splice(nextStage.steps.length, 0, step);

      this.scrollToElementByIdWhenCreated(this.getStepElementId(nextStage.order, step.order));
      this.change();

    } else {
      return;
    }
  }

  moveStepDown(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    const stepIndex: number = stage.steps.findIndex(x => x.order === stepOrder);

    if (stepOrder < stage.steps.length) {
      const stepOrder2 = stepOrder + 1;
      const step2: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder2);
      const stepIndex2: number = stage.steps.findIndex(x => x.order === stepOrder2);

      step.order++;
      step2.order--;

      stage.steps[stepIndex] = step2;
      stage.steps[stepIndex2] = step;

      this.scrollToElementByIdWhenCreated(this.getStepElementId(stageOrder, step.order));
      this.change();
    } else if (stepOrder === stage.steps.length && stage.steps.length > 1 && stage.order < this.process.stages.length) {

      const nextStage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder + 1);

      stage.steps.splice(stepIndex, 1);

      step.order = 1;

      nextStage.steps.splice(0, 0, step);

      for (let i = 1; i < nextStage.steps.length; i++) {
        nextStage.steps[i].order++;
      }
      this.scrollToElementByIdWhenCreated(this.getStepElementId(nextStage.order, 1));
      this.change();
    } else {
      return;
    }
  }

  moveStageUp(stageOrder: number): void {
    if (stageOrder < 2) {
      return;
    }
    const stageOrder2 = stageOrder - 1;
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const stage2: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder2);
    const stageIndex: number = this.process.stages.findIndex(x => x.order === stageOrder);
    const stageIndex2: number = this.process.stages.findIndex(x => x.order === stageOrder2);

    stage.order--;
    stage2.order++;

    this.process.stages[stageIndex] = stage2;
    this.process.stages[stageIndex2] = stage;

    this.scrollToElementByIdWhenCreated(this.getStepElementId(stage.order, 1));
    this.change();
  }

  moveStageDown(stageOrder: number): void {
    if (stageOrder > this.process.stages.length || stageOrder === this.process.stages.length) {
      return;
    }
    const stageOrder2 = stageOrder + 1;
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const stage2: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder2);
    const stageIndex: number = this.process.stages.findIndex(x => x.order === stageOrder);
    const stageIndex2: number = this.process.stages.findIndex(x => x.order === stageOrder2);

    stage.order++;
    stage2.order--;

    this.process.stages[stageIndex] = stage2;
    this.process.stages[stageIndex2] = stage;

    this.scrollToElementByIdWhenCreated(this.getStepElementId(stage.order, 1));
    this.change();
  }

  duplicateStage(stageOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const stageIndex: number = this.process.stages.findIndex(x => x.order === stageOrder);
    const newStage: Models.ProcessStage = { title: stage.title, order: stage.order, steps: [] };

    for (let i = 0; i < stage.steps.length; i++) {
      let newStep = Object.assign({}, stage.steps[i]);
      newStep.tasks = stage.steps[i].tasks.slice(0);
      newStage.steps.push(newStep);
    }

    this.process.stages.splice(stageIndex + 1, 0, newStage);

    for (let i = stageIndex + 1; i < this.process.stages.length; i++) {
      this.process.stages[i].order++;
    }

    this.change();
    this.scrollToElementByIdWhenCreated(this.getStageElementId(stageOrder + 1));
  }

  deleteStage(stageOrder: number): void {
    if (this.process.stages.length <= 1) {
      this.translateService.get('PROCESS.EDIT.STAGE_DELETE_ERROR').subscribe((translatedMsg) => {
        this.toastrService.error(translatedMsg);
      });
      return;
    }
    const stageIndex: number = this.process.stages.findIndex(x => x.order === stageOrder);

    this.process.stages.splice(stageIndex, 1);

    for (let i: number = stageIndex + 1; i < this.process.stages.length; i++) {
      this.process.stages[i].order--;
    }

    this.change();
  }

  addStage(stageOrder: number): void {
    const stageIndex: number = this.process.stages.findIndex(x => x.order === stageOrder);
    const newStage: Models.ProcessStage = new Models.ProcessStage();
    newStage.order = stageOrder;

    this.process.stages.splice(stageIndex + 1, 0, newStage);

    for (let i: number = stageIndex + 1; i < this.process.stages.length; i++) {
      this.process.stages[i].order++;
    }
    this.scrollToElementByIdWhenCreated(this.getStageElementId(stageOrder + 1));
  }

  addStep(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const stepIndex: number = stage.steps.findIndex(x => x.order === stepOrder);
    const newStep: Models.ProcessStep = new Models.ProcessStep();
    newStep.order = stepOrder;

    stage.steps.splice(stepIndex + 1, 0, newStep);

    for (let i: number = stepIndex + 1; i < stage.steps.length; i++) {
      stage.steps[i].order++;
    }
    this.scrollToElementByIdWhenCreated(this.getStepElementId(stageOrder, stepOrder + 1));
  }

  getTitleAutocompletePlaceholder(stepTypeId?: number) {
    if (!stepTypeId) {
      return this.titleAutoCompletePlaceHolder_NoStepType;
    } else {
      return this.titleAutoCompletePlaceHolder;
    }
  }

  addMedia(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    const currentMedia = step.medias && step.medias.length > 0 ? step.medias[0] : null;

    const data: Models.ModalContext = {
      modalType: Models.ModalType.MEDIA_UPLOAD,
      modalAction: Models.ModalAction.OPEN,
      parameters: [this.processId, this.processMedias, Models.MediaModalModes.IMAGE_AND_VIDEO, currentMedia]
    };
    this.modalService.command(data);

    const subscription = this.modalService.returnEvent$.subscribe((resultData: Models.ModalResult) => {
      if (resultData.modalType === Models.ModalType.MEDIA_UPLOAD) {
        const media: Models.Media = <Models.Media>resultData.result;
        if (media) {
          step.medias = [media];
          this.change();
        }
        data.modalAction = Models.ModalAction.CLOSE;
        this.modalService.command(data);
        subscription.unsubscribe();
      }
    });
  }

  removeMedia(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    step.medias = [];
    this.change();
  }

  public getStepMedia(stageOrder: number, stepOrder: number): Models.Media {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    return step.medias.length ? step.medias[0] : null;
  }

  public getMediaImage(media: Models.Media): string {
    if (!media) {
      return '';
    }
    // @TODO: replace numbers with enum
    if (media.mediaType === 1) {
      return media.smallUrl;
    } else if (media.mediaType === 2) {
      return (this.getVideoThumbnail(media));
    }
    return '';
  }

  getVideoId(url: string): string {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  }

  getVideoThumbnail(video: Models.Media): string {
    if (!video.mediaUrl) {
      return '';
    }
    const videoId: string = this.getVideoId(video.mediaUrl);
    if (!videoId) {
      return '';
    }
    // @TODO: move base url to config file
    return 'https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg';
  }

  comingSoon(): void {
    this.translateService.get('PROCESS.EDIT.COMING_SOON').subscribe((translatedMsg) => {
      this.toastrService.info(translatedMsg);
    });
  }

  addCheckList(stageOrder: number, stepOrder: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);

    if (step.showTasks) {
      return;
    } else {
      step.showTasks = true;
      step.tasks.push(this.checkBoxText + ' 1');
      this.focusInputByIdWhenCreated('task_input_' + stage.order + '_' + step.order + '_0');
      this.change();
    }
  }

  addNewTask($event: Event, stageOrder: number, stepOrder: number, index?: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);

    index = index !== undefined ? (index + 1) : step.tasks.length;

    step.tasks.splice(index, 0, this.checkBoxText + ' ' + (index + 1).toString());

    step.newTaskTitle = '';

    this.focusInputByIdWhenCreated('task_input_' + stage.order + '_' + step.order + '_' + (index));

    this.change();
  }

  removeTask(stageOrder: number, stepOrder: number, index: number): void {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);

    step.tasks.splice(index, 1);
    this.change();

    if (step.tasks.length === 0) {
      step.showTasks = false
    }
  }

  trackTaskItemChanges(index: number) {
    return +index;
  }

  deleteDraft(): void {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.CONFIRMATION,
      modalAction: Models.ModalAction.OPEN,
      parameters: ['deleteDraftTitle', 'deleteDraftMsg1', 'deleteDraftMsg2']
    };
    this.modalService.command(data);

    const subscription = this.modalService.returnEvent$.subscribe((result: boolean) => {
      subscription.unsubscribe();
      const confData: Models.ModalContext = {
        modalType: Models.ModalType.CONFIRMATION,
        modalAction: Models.ModalAction.CLOSE,
        parameters: []
      };

      if (result) {
        this.processService.deleteDraft(this.processId)
          .finally(() => { })
          .subscribe(() => {
            this.translateService.get([
              'PROCESS.EDIT.DRAFT_DELETED_SUCCESSFULLY', 'PROCESS.EDIT.SUCCESS'])
              .subscribe((translatedTxt) => {
                this.modalService.command(confData);
                this.toastrService.success(translatedTxt['PROCESS.EDIT.DRAFT_DELETED_SUCCESSFULLY'], translatedTxt['PROCESS.EDIT.SUCCESS']);
                this.router.navigate(['/home']);
              });
          }, error => {
            this.translateService.get('PROCESS.EDIT.GENERAL_ERROR').subscribe((errMsg) => {
              this.modalService.command(confData);
              this.error = error;
              this.toastrService.error(this.errorMessage, errMsg);
            });
          });
      } else {
        data.modalAction = Models.ModalAction.CLOSE;
        this.modalService.command(data);
      }
    });
  }

  directionChange(): void {
    this.isLoading = true;
    this.processLanguage = !this.process.isRTL ? Models.Languages.enUS : Models.Languages.heIL;
    this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, this.processLanguage);
    this.translateTexts();
    this.getStepTypeData();
    this.change();
  }

  // ease in out function thanks to:
  // http://blog.greweb.fr/2012/02/bezier-curve-based-easing-functions-from-concept-to-implementation/
  easeInOutCubic(t: number): number {
    return t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  }

  private getStageElementId(stageOrder: number): string {
    return 'stageDetails_' + stageOrder.toString();
  }

  private getStepElementId(stageOrder: number, stepOrder: number): string {
    return 'stepDetails_' + stageOrder.toString() + '_' + stepOrder.toString();
  }

  private focusInputById(id: string): void {
    const element: HTMLInputElement = <HTMLInputElement>this.getTarget(id);
    if (element) {
      element.focus();
      element.select();
    }
  }

  private focusInputByIdWhenCreated(id: string): void {
    const interval = setInterval(() => {
      if (this.getTarget(id)) {
        clearInterval(interval);
        this.focusInputById(id);
      }
    }, 50);
  }

  private translateTexts(): void {

    const translateKeys = [
      'PROCESS.EDIT.AUTO_COMPLETE_PLACEHOLDER',
      'PROCESS.EDIT.AUTO_COMPLETE_PLACEHOLDER_NO_STEP_TYPE',
      'PROCESS.EDIT.STEP_EDITOR_PLACEHOLDER',
      'PROCESS.EDIT.ITEM',
      'PROCESS.EDIT.GENERAL_ERROR'
    ];

    this.translateService.get(translateKeys).subscribe((valueArr: string) => {
      this.titleAutoCompletePlaceHolder = valueArr['PROCESS.EDIT.AUTO_COMPLETE_PLACEHOLDER'];
      this.titleAutoCompletePlaceHolder_NoStepType = valueArr['PROCESS.EDIT.AUTO_COMPLETE_PLACEHOLDER_NO_STEP_TYPE'];
      this.textHtmlEditorPlaceHolder = valueArr['PROCESS.EDIT.STEP_EDITOR_PLACEHOLDER'];
      this.checkBoxText = valueArr['PROCESS.EDIT.ITEM'];
      this.generalErrorMessage = valueArr['PROCESS.EDIT.GENERAL_ERROR'];
    });
  }

  private createStepTypeSelect(): void {
    if (this.stepTypes.length > 0) {
      this.stepTypesSelect = this.stepTypes.map(x => ({ value: x.stepTypeId, label: x.name, icon: x.imageUrl }));
    }
  }
  //#region Scroll Functions
  private scrollToElementById(id: string): void {
    let scrollEnd: number;
    let target: HTMLElement;
    if (scrollEnd === null || scrollEnd === undefined) {
      target = this.getTarget(id);
      if (!target) {
        console.warn('target element do not exist');
        return;
      }
      if (target.className.lastIndexOf('stepContent') < 0) {
        scrollEnd = target.offsetTop;
      } else {
        const parentElement: HTMLElement = this.getCardParent(target);
        scrollEnd = parentElement ? parentElement.offsetTop + target.offsetTop : target.offsetTop;
      }
    }
    const scrollingElement: HTMLElement = this.getScrollableElement(target);
    try {
      if (scrollingElement === document.body) {
        this.smoothScroll(document.documentElement, scrollEnd);
      }
    } catch (e) { console.warn(e) }

    this.smoothScroll(scrollingElement, scrollEnd);
  }

  private scrollToElementByIdWhenCreated(id: string): void {
    const interval = setInterval(() => {
      if (this.getTarget(id)) {
        clearInterval(interval);
        this.scrollToElementById(id);
      }
    }, 50);
  }

  private getScrollableElement(target: HTMLElement): HTMLElement {
    let scrollableElement: HTMLElement;
    if (target != null) {
      scrollableElement = this.findScrollableParent(target);
    } else {
      scrollableElement = this.findMainScrollableElement();
    }
    return scrollableElement;
  }

  private getTarget(id: string): HTMLElement {
    let target: HTMLElement;
    const href: string = '#' + id;
    target = <HTMLElement>document.querySelector(href);
    return target;
  }

  private smoothScroll(element: HTMLElement, end: number): void {
    const duration = 500;
    const clock: number = Date.now();
    const requestAnimationFrame = window.requestAnimationFrame || function (fn: object) {
      window.setTimeout(fn, 15);
    };

    const start: number = element.scrollTop;
    const step = () => {
      const elapsed = Date.now() - clock;
      const position = this.position(start, end, elapsed, duration);
      element.scrollTop = position;
      if (elapsed > duration) {
      } else {
        requestAnimationFrame(step);
      }
    };
    step();
  }


  /**
   * calculate the scroll position we should be in
   * given the start and end point of the scroll
   * the time elapsed from the beginning of the scroll
   * and the total duration of the scroll (default 500ms)
   */
  private position(start: number, end: number, elapsed: number, duration: number): number {
    if (elapsed > duration) {
      return end;
    }
    return start + (end - start) * this.easeInOutCubic(elapsed / duration); // <-- you can change the easing funtion there
    // return start + (end - start) * (elapsed / duration); // <-- this would give a linear scroll
  }


  /**
   * finds scrollable parent of an element
   * @method findScrollableParent
   * @param {HTMLElement} element
   * @returns {HTMLElement} element
   */
  private findScrollableParent(element: HTMLElement): HTMLElement {
    let isBody: boolean,
      hasScrollableSpace: boolean,
      hasVisibleOverflow: boolean;
    do {
      element = element.parentElement;
      // set condition variables
      isBody = element === document.body;
      hasScrollableSpace = element.clientHeight < element.scrollHeight;
      hasVisibleOverflow = getComputedStyle(element, null).overflow === 'visible';
    } while (!isBody && !(hasScrollableSpace && !hasVisibleOverflow));
    return element;
  }

  private getCardParent(element: HTMLElement): HTMLElement {
    if (element.className.lastIndexOf('stepContent') < 0) {
      return null;
    }

    do {
      element = element.parentElement;
      // set condition variables
      if (element === document.body) {
        return null;
      }
    } while (element != null && element.className.lastIndexOf('stageDetailsCard') < 0);
    return element;
  }


  /**
   * finds scrollable parent of an element
   * @method findMainScrollableElement
   * @returns {HTMLElement} element
   */
  private findMainScrollableElement(): HTMLElement {
    const element: HTMLElement = this.findScrollableChild(document.body);
    if (element != null) {
      return element;
    }
    return document.body;
  }

  private isScrollable(element: HTMLElement): boolean {
    const hasScrollableSpace = element.clientHeight < element.scrollHeight;
    const hasVisibleOverflow = getComputedStyle(element, null).overflow === 'visible';
    return hasScrollableSpace && !hasVisibleOverflow;
  }

  private isScriptTag(element: HTMLElement): boolean {
    return element.nodeName === 'SCRIPT';
  }

  private findScrollableChild(inputElement: HTMLElement): HTMLElement {
    let scrollableElement: HTMLElement;
    let i = 0;
    if (this.isScriptTag(inputElement)) {
      return null;
    }
    while (scrollableElement == null && i < inputElement.childElementCount) {
      const element = <HTMLElement>inputElement.children[i];
      if (this.isScrollable(element)) {
        scrollableElement = element;
        return element;
      }
      scrollableElement = this.findScrollableChild(element);
      i++;
    }
    return scrollableElement;
  }
  //#endregion
}



// WEBPACK FOOTER //
// C:/ProactiView/ProactiView-Client/src/app/editProcess/editProcess.component.ts